import { CardsDataSourceBase } from '../../common/CardsDataSourceBase';
import { injectable } from 'inversify';
import { injectToken } from 'inversify-token';
import { YinzCamAPIResponse } from 'yinzcam-api';
import { YinzCamAppServerToken } from 'yinzcam-appserver';

type CricketOutcome = '4' | '6' | 'W' | '.' | '0' | '1' | '2' | '3' | '5' | 'WD' | 'NB' | 'LB' | 'B';

function normalizeCricketBallToken(raw: unknown): CricketOutcome | '.' {
  const s = String(raw ?? '').trim();
  if (!s || s === '-') return '.';
  const upper = s.toUpperCase();
  return (upper as CricketOutcome) ?? '.';
}

function deriveCricketRunsLabel(outcome: string | undefined | null, runs?: number): string {
  const key = String(outcome ?? '').trim().toUpperCase();
  if (key === 'W') return 'Out!';
  if (key === 'WD') return 'Wide!';
  if (key === 'NB') return 'No ball.';
  if (key === '6') return 'Six!';
  if (key === '4') return '4 runs.';
  if (key === '.' || key === '0') return '0 runs.';
  const n = typeof runs === 'number' ? runs : Number(key);
  if (!Number.isFinite(n)) return '0 runs.';
  return n === 1 ? '1 run.' : `${n} runs.`;
}

// xml-js compact:true, nativeType:true coerces text nodes but NOT attributes
// (no nativeTypeAttributes), so attribute booleans/numbers arrive as strings.
// Priority on each <Ball>: Wide > NoBall > Wicket > Runs.
function deriveOutcomeFromBallAttrs(attrs: Record<string, any>): CricketOutcome | '.' {
  const isTrueish = (v: any) => v === true || v === 'true';
  if (isTrueish(attrs?.Wide)) return 'WD';
  if (isTrueish(attrs?.NoBall)) return 'NB';
  if (isTrueish(attrs?.Wicket)) return 'W';
  const runs = Number(attrs?.Runs ?? 0);
  if (!Number.isFinite(runs) || runs === 0) return '.';
  return String(runs) as CricketOutcome;
}

function buildOver(over: any) {
  const overAttributes = over?._attributes ?? {};
  const overNumber = overAttributes?.OverNumber ?? '';
  const totalRuns = overAttributes?.TotalRuns;
  const bowlerName = overAttributes?.BowlerName ?? '';

  // Index any <Ball> children by their Number attribute so we can pair each
  // B1..Bn slot with commentary-bearing data. Dot balls may be absent.
  const byNumber = new Map<number, any>();
  []
    .concat(over?.Ball)
    .filter(Boolean)
    .forEach((overBall: any) => {
      const ballNumber = Number(overBall?._attributes?.Number);
      if (Number.isFinite(ballNumber)) byNumber.set(ballNumber, overBall);
    });

  const ballKeys = Object.keys(overAttributes)
    .filter((k) => /^B\d+$/.test(k))
    .sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10));

  const balls = ballKeys.map((ballIndex) => {
    const slot = parseInt(ballIndex.slice(1), 10);
    const ball = byNumber.get(slot);
    const ballAttrs = ball?._attributes ?? {};

    const outcome: CricketOutcome | '.' = ball
      ? deriveOutcomeFromBallAttrs(ballAttrs)
      : normalizeCricketBallToken(overAttributes[ballIndex]);

    const runs = typeof ballAttrs?.Runs === 'number' ? ballAttrs.Runs : Number(ballAttrs?.Runs ?? 0);
    const timestamp = ballAttrs?.BallDisplay ?? (overNumber !== '' ? `${overNumber}.${slot}` : '');

    return {
      outcome,
      timestamp: String(timestamp),
      bowler: ballAttrs?.BowlerName ?? bowlerName ?? '',
      batter: ballAttrs?.BatterName ?? '',
      runsLabel: deriveCricketRunsLabel(outcome, Number.isFinite(runs) ? runs : undefined),
      commentary: ballAttrs?.Commentary ?? '',
    };
  });

  const runsNum = typeof totalRuns === 'number' ? totalRuns : Number(totalRuns);
  const runsWord = Number.isFinite(runsNum) && runsNum === 1 ? 'run' : 'runs';
  const summaryText = totalRuns !== undefined && bowlerName ? `${totalRuns} ${runsWord} | b: ${bowlerName}` : '';

  return {
    id: overNumber !== '' ? `over-${overNumber}` : undefined,
    overLabel: overNumber !== '' ? `Over ${overNumber}` : 'Over',
    summaryText,
    balls,
  };
}

@injectable()
export class YinzCamAppServerBallByBall extends CardsDataSourceBase {
  public constructor(@injectToken(YinzCamAppServerToken) server) {
    super({ server });
  }

  protected processResponse(
    path: string,
    response: YinzCamAPIResponse,
    specData?: { [key: string]: any }
  ): object | any[] {
    const data = response?.data as any;
    const commentary = data?.Commentary;

    const rawInnings = [].concat(commentary?.Innings).filter(Boolean);

    const innings = rawInnings.map((inning: any) => {
      const inningAttributes = inning?._attributes ?? {};
      const tricode = inningAttributes?.TeamTriCode ?? inningAttributes?.TeamName ?? '';
      const label = tricode ? `${tricode} INNINGS` : '';

      const rawOvers = [].concat(inning?.Over).filter(Boolean);
      const overs = rawOvers.map((over: any) => buildOver(over));

      return { label, overs };
    });

    if (specData?.checkAvaliable) {
      return { dataReady: innings.length > 0 };
    }

    return { innings };
  }
}
